import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import { FormaDateBalanceSheet } from "../../../Common/FormatFunctions/FormatDate"
import PdfPreview from '../../../Common/PDFPreview'
import { formatReadableAmount } from '../../../Common/FormatFunctions/FormatAmount';
import { RingLoader } from 'react-spinners';

const apikey = process.env.REACT_APP_API;

const SaleBillBalanceReport = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const fromDate = searchParams.get('fromDate');
    const toDate = searchParams.get('toDate');
    const acCode = searchParams.get('acCode')
    const companyCode = sessionStorage.getItem("Company_Code");
    const Year_Code = sessionStorage.getItem("Year_Code");
    const Company_Name = sessionStorage.getItem('Company_Name')
    const Company_GSTNO = sessionStorage.getItem('Company_GSTNO')

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [emailId, setEmailId] = useState('');
    const [popconfirmvalue, setPopconfirmvalue] = useState('Y');

    const [pdfPreview, setPdfPreview] = useState(null);

    const API_URL = `${apikey}/get-sale-bill-summary`;

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    };



    useEffect(() => {
        const fetchReportData = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await axios.get(API_URL, {
                    params: {
                        Company_Code: companyCode,
                        Year_Code: Year_Code,
                        popconfirmvalue: popconfirmvalue,
                        from_date: fromDate,
                        to_date: toDate,
                        accode: acCode
                    },
                });

                const resData = response.data;
                const finalData = Array.isArray(resData) ? resData : resData.data || [];
                setReportData(finalData);
            } catch (error) {
                console.error('Error fetching report:', error);
                setError('Error fetching report');
            } finally {
                setLoading(false);
            }
        };

        fetchReportData();
    }, [API_URL, fromDate, toDate, companyCode, Year_Code, acCode, popconfirmvalue]);

    const handleExportToExcel = () => {
        const groupedData = groupReportData(reportData);
        const excelData = [];

        Object.entries(groupedData).forEach(([key, group]) => {
            group.items.forEach(item => {
                excelData.push({
                    "Doc Date": formatDate(item.doc_date) || item.doc_dateConverted,
                    "Bill No": item.billno,
                    "Quantal": item.NETQNTL,
                    "Bill Type": item.bill_tran_type || '',
                    "Adj Amount": item.adjAmt || '',
                    "OnAc": item.OnAc || '',
                    "Bill Amount": Number(item.Bill_Amount),
                    "Received": Number(item.received),
                    "Balance": Number(item.balance),
                    "Broker": item.brokername || '',
                    "Lorry No": item.LORRYNO || '',
                });
            });

            excelData.push({
                "Bill No": 'Total',
                "Qty": Number(group.totalQty)
            });
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);

        XLSX.utils.book_append_sheet(wb, ws, 'Normal Sauda');
        XLSX.writeFile(wb, 'NormalSaudaSummary.xlsx');
    };

    const handlePrint = () => {
        const groupedData = groupReportData(reportData);

        const formatRow = (cells, isHeader = false) => {
            return `<tr>${cells.map((cell, index) => {
                let align = index >= 7 ? 'right' : (index === 1 || index === 4) ? 'left' : 'center';
                const style = isHeader
                    ? `background-color:#e0e0e0;font-weight:bold;text-align:${align};padding:4px;border:1px solid #ccc;`
                    : `text-align:${align};padding:4px;border:1px solid #ccc;`;
                return `<td style="${style}">${cell ?? ''}</td>`;
            }).join('')}</tr>`;
        };

        let content = `<html><head><style>
            body { font-family: Arial; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ccc; padding: 6px; font-size: 11px; }
            .group-header { background-color: #f2f2f2; font-weight: bold; text-align: center; padding: 6px; }
        </style></head><body>
        <h2 style="text-align:center;">${Company_Name}</h2>
        <h4 style="text-align:center;">Normal Sauda Summary - From ${formatDate(fromDate)} To ${formatDate(toDate)}</h4>`;

        Object.entries(groupedData).forEach(([key, group]) => {
            const [buyerCode, buyerName] = key.split('-');
            content += `<div class="group-header">${buyerCode} - ${buyerName}</div>`;
            content += `<table><thead>${formatRow([
                'Bill Date', 'Bill No', 'Quantal', 'Bill Type', 'Adj Amount', 'OnAc', 'Bill Amount', 'Received',
                'Balance', 'Broker', 'Qty', 'Lorry No'
            ], true)}</thead><tbody>`;

            group.items.forEach(item => {
                content += formatRow([
                    formatDate(item.doc_date) || item.doc_dateConverted,
                    item.billno,
                    item.NETQNTL,
                    item.bill_tran_type || '',
                    item.adjAmt || '',
                    item.OnAc || '',
                    formatReadableAmount(item.Bill_Amount),
                    formatReadableAmount(item.received),
                    formatReadableAmount(item.balance),
                    item.brokername || '',
                    item.LORRYNO || '',
                ]);
            });

            content += `<tr><td colspan="10" style="text-align:right;font-weight:bold;">Total Qty:</td>
                        <td colspan="5" style="text-align:right;font-weight:bold;color:red;">${formatReadableAmount(group.totalQty)}</td></tr></tbody></table>`;
        });

        content += '</body></html>';
        const win = window.open('', '', 'height=900,width=1200');
        win.document.write(content);
        win.document.close();
        win.print();
    };


    const generatePDF = async () => {
        const doc = new jsPDF('l');
        const pageWidth = doc.internal.pageSize.width;
        const groupedData = groupReportData(reportData);
        let currentY = 10;

        doc.setFontSize(10);
        doc.text(`${Company_Name}`, pageWidth / 2, currentY, { align: "center" });
        currentY += 5;
        doc.setFontSize(8);
        doc.text(`Normal Sauda Summary - From ${formatDate(fromDate)} To ${formatDate(toDate)}`, pageWidth / 2, currentY, { align: "center" });

        const tableBody = [];

        Object.entries(groupedData).forEach(([key, group]) => {
            const [buyerCode, buyerName] = key.split('-');
            tableBody.push([{
                content: `${buyerCode} - ${buyerName}`,
                colSpan: 15,
                styles: { halign: 'center', fontStyle: 'bold', fillColor: [230, 230, 250] }
            }]);

            group.items.forEach(item => {
                tableBody.push([
                    formatDate(item.doc_date) || item.doc_dateConverted,
                    item.billno,
                    item.NETQNTL,
                    item.bill_tran_type || '',
                    item.adjAmt || '',
                    item.OnAc || '',
                    formatReadableAmount(item.Bill_Amount),
                    formatReadableAmount(item.received),
                    formatReadableAmount(item.balance),
                    item.brokername || '',
                    item.LORRYNO || '',
                ]);
            });

            tableBody.push([
                { content: '', colSpan: 10 },
                { content: formatReadableAmount(group.totalQty), styles: { halign: 'right', fontStyle: 'bold' } },
                '', '', '', ''
            ]);
        });

        doc.autoTable({
            head: [[
                'Bill Date', 'Bill No', 'Quantal', 'Bill Type', 'Adj Amount', 'OnAc', 'Bill Amount', 'Received',
                'Balance', 'Broker', 'Qty', 'Lorry No'
            ]],
            body: tableBody,
            startY: currentY + 3,
            styles: {
                fontSize: 6,
                cellPadding: 0.6,
                overflow: 'visible'
            },
            columnStyles: {
                0: { halign: 'center' },
                1: { halign: 'center' },
                2: { halign: 'center' },
                3: { halign: 'center' },
                4: { halign: 'left' },
                5: { halign: 'left' },
                6: { halign: 'left' },
                7: { halign: 'right' },
                8: { halign: 'right' },
                9: { halign: 'left' },
                10: { halign: 'right' },
                11: { halign: 'center' },
                // 12: { halign: 'right' },
                // 13: { halign: 'center' },
                // 14: { halign: 'left' },
            },
            headStyles: {
                fillColor: [200, 200, 200],
                textColor: [0, 0, 0],
                fontStyle: 'bold'
            }
        });

        const blob = doc.output("blob");
        const pdfUrl = URL.createObjectURL(blob);
        setPdfPreview(pdfUrl);
    };


    const groupReportData = (data) => {
        const groupedData = {};
        (data || []).forEach((item) => {
            const key = `${item.billtoname}`;
            if (!groupedData[key]) {
                groupedData[key] = { items: [], totalQty: 0, BalanceTotal: 0, BillAmtTotal: 0, RecievedTotal: 0 };
            }
            groupedData[key].items.push(item);
            groupedData[key].totalQty += parseFloat(item.NETQNTL || 0);
            groupedData[key].BalanceTotal += parseFloat(item.balance || 0);
            groupedData[key].BillAmtTotal += parseFloat(item.Bill_Amount || 0);
            groupedData[key].RecievedTotal += parseFloat(item.received || 0);


        });

        return groupedData;
    };

    const groupedReportData = groupReportData(reportData);

    return (
        <div style={{ marginTop: "-80px" }}>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "24px", fontWeight: "bold" }}>{Company_Name}</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px", textDecoration: 'underline', fontWeight: "550" }}>GSTN : {Company_GSTNO}</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "20px", fontWeight: "bold" }}>Sale Balance Summary</Typography>
            <Typography variant="h6" style={{ textAlign: 'center', fontSize: "16px" }}>{FormaDateBalanceSheet(fromDate)} to {FormaDateBalanceSheet(toDate)}</Typography>

            <div className="row align-items-center mb-3">
                <div className="col-auto">
                    <button className="btn btn-secondary me-2" onClick={handlePrint}>Print</button>
                    <button className="btn btn-success me-2" onClick={handleExportToExcel}>Export to Excel</button>
                    {pdfPreview && <PdfPreview pdfData={pdfPreview} apiData={reportData[0]} label="NormalSaudaSummary" />}
                    <button className="btn btn-success" onClick={generatePDF}>PDF Preview</button>
                </div>
            </div>

            <TableContainer component={Paper} sx={{ maxHeight: '80vh', overflow: 'auto' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ backgroundColor: "#f0f0f0", fontWeight: "bold", border: "1px solid #ccc", position: "sticky", top: 0 }}>Bill Date</TableCell>

                            <TableCell sx={{ backgroundColor: "#f0f0f0", fontWeight: "bold", border: "1px solid #ccc", position: "sticky", top: 0 }}>Bill No</TableCell>

                            <TableCell sx={{ backgroundColor: "#f0f0f0", fontWeight: "bold", border: "1px solid #ccc", position: "sticky", top: 0 }}>Quantal</TableCell>

                            <TableCell sx={{ backgroundColor: "#f0f0f0", fontWeight: "bold", border: "1px solid #ccc", position: "sticky", top: 0 }}>Bill Type</TableCell>

                            <TableCell sx={{ backgroundColor: "#f0f0f0", fontWeight: "bold", border: "1px solid #ccc", position: "sticky", top: 0, textAlign: "right" }}>Adj Amount</TableCell>

                            <TableCell sx={{ backgroundColor: "#f0f0f0", fontWeight: "bold", border: "1px solid #ccc", position: "sticky", top: 0, textAlign: "right" }}>OnAc</TableCell>

                            <TableCell sx={{ backgroundColor: "#f0f0f0", fontWeight: "bold", border: "1px solid #ccc", position: "sticky", top: 0, textAlign: "right" }}>Bill Amount</TableCell>

                            <TableCell sx={{ backgroundColor: "#f0f0f0", fontWeight: "bold", border: "1px solid #ccc", position: "sticky", top: 0, textAlign: "right" }}>Received</TableCell>

                            <TableCell sx={{ backgroundColor: "#f0f0f0", fontWeight: "bold", border: "1px solid #ccc", position: "sticky", top: 0, textAlign: "right" }}>Balance</TableCell>

                            <TableCell sx={{ backgroundColor: "#f0f0f0", fontWeight: "bold", border: "1px solid #ccc", position: "sticky", top: 0 }}>Broker</TableCell>

                            <TableCell sx={{ backgroundColor: "#f0f0f0", fontWeight: "bold", border: "1px solid #ccc", position: "sticky", top: 0 }}>Lorry No</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {Object.entries(groupedReportData).map(([key, { items, totalQty, BalanceTotal, BillAmtTotal, RecievedTotal }]) => {
                            const [Ac_Code, billtoname] = key.split("-");
                            return (
                                <React.Fragment key={key}>
                                    <TableRow>
                                        <TableCell colSpan={15} sx={{ backgroundColor: "#e6f0ff", fontWeight: "bold", color: "blue" }}>
                                            {Ac_Code} - {billtoname}
                                        </TableCell>
                                    </TableRow>

                                    {items.map((item, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{formatDate(item.doc_date) || item.doc_dateConverted}</TableCell>
                                            <TableCell>{item.billno}</TableCell>
                                            <TableCell>{item.NETQNTL}</TableCell>
                                            <TableCell>{item.bill_tran_type}</TableCell>

                                            <TableCell align="right">{item.adjAmt}</TableCell>
                                            <TableCell align="right">{item.OnAc}</TableCell>
                                            <TableCell align="right">{formatReadableAmount(item.Bill_Amount)}</TableCell>
                                            <TableCell align="right">{formatReadableAmount(item.received)}</TableCell>
                                            <TableCell align="right">{formatReadableAmount(item.balance)}</TableCell>
                                            <TableCell align="left">{item.brokername}</TableCell>
                                            <TableCell align="left">{item.LORRYNO}</TableCell>

                                        </TableRow>
                                    ))}

                                    <TableRow>
                                        <TableCell colSpan={2} align="right" sx={{ fontWeight: "bold" }}>
                                            Total
                                        </TableCell>
                                        <TableCell align="left" sx={{ fontWeight: "bold" }}>
                                            {formatReadableAmount(totalQty)}
                                        </TableCell>
                                        <TableCell colSpan={4} align="right"> {formatReadableAmount(BillAmtTotal)} </TableCell>
                                        <TableCell align="right"> {formatReadableAmount(RecievedTotal)} </TableCell>
                                        <TableCell align="right"> {formatReadableAmount(BalanceTotal)} </TableCell>

                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {loading && (
                <div
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 9999,
                    }}
                >
                    <RingLoader size={80} />
                </div>
            )}
            {error && <div className="alert alert-danger">{error}</div>}
        </div>
    );

};

export default SaleBillBalanceReport;
